import React, { useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { BarChart3, Facebook, Check, Copy, ExternalLink, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPreferences() {
  const [copied, setCopied] = useState(null);

  const feedUrl = `${API}/api/marketing/facebook-feed`;
  const gaId = 'G-55G1L5KSKL';

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-preferences">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Globe className="w-7 h-7 text-orange-500" />
            Preferencias
          </h1>
          <p className="text-gray-400 mt-1">Integraciones de marketing y analytics</p>
        </div>

        {/* Google Analytics */}
        <Card className="bg-gray-800 border-gray-700 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">Google Analytics (GA4)</h3>
              <p className="text-sm text-gray-400">Seguimiento de visitas y conversiones</p>
            </div>
            <Badge className="bg-green-600/20 text-green-400 border-green-600/30">Activo</Badge>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1.5">Measurement ID</label>
              <div className="flex gap-2">
                <Input value={gaId} readOnly className="bg-gray-700 border-gray-600 text-white font-mono" data-testid="ga-id-input" />
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700 shrink-0" onClick={() => copyToClipboard(gaId, 'ga')}>
                  {copied === 'ga' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="bg-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-300 mb-2">Eventos configurados:</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="border-gray-600 text-gray-300">page_view</Badge>
                <Badge variant="outline" className="border-orange-600 text-orange-400">purchase (conversion)</Badge>
              </div>
              <p className="text-xs text-gray-500 mt-3">El evento <code className="text-orange-400">purchase</code> se dispara al completar una orden en el checkout, enviando <code className="text-gray-400">transaction_id</code> y <code className="text-gray-400">value</code>.</p>
            </div>
          </div>
        </Card>

        {/* Facebook Feed */}
        <Card className="bg-gray-800 border-gray-700 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Facebook className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">Facebook Commerce / Ads</h3>
              <p className="text-sm text-gray-400">Feed XML de productos para catalogo de Facebook</p>
            </div>
            <Badge className="bg-green-600/20 text-green-400 border-green-600/30">Activo</Badge>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1.5">URL del Feed (XML)</label>
              <div className="flex gap-2">
                <Input value={feedUrl} readOnly className="bg-gray-700 border-gray-600 text-white font-mono text-sm" data-testid="feed-url-input" />
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700 shrink-0" onClick={() => copyToClipboard(feedUrl, 'feed')} data-testid="copy-feed-btn">
                  {copied === 'feed' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </Button>
                <a href={feedUrl} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700 shrink-0">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>

            <div className="bg-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-300 mb-2">Instrucciones:</p>
              <ol className="text-sm text-gray-400 space-y-1.5 list-decimal list-inside">
                <li>Copia la URL del feed</li>
                <li>Ve a <strong className="text-gray-300">Facebook Commerce Manager</strong> &gt; Catalogos &gt; Fuentes de datos</li>
                <li>Selecciona "Agregar productos" &gt; "Feed de datos" &gt; "Programado"</li>
                <li>Pega la URL y configura la frecuencia de actualizacion</li>
              </ol>
              <p className="text-xs text-gray-500 mt-3">Campos incluidos: id, title, description, image_link, link, price, availability, condition, brand, product_type</p>
            </div>
          </div>
        </Card>

        {/* Meta Pixel */}
        <Card className="bg-gray-800 border-gray-700 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Facebook className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">Meta Pixel</h3>
              <p className="text-sm text-gray-400">Seguimiento de conversiones y remarketing</p>
            </div>
            <Badge className="bg-green-600/20 text-green-400 border-green-600/30">Activo</Badge>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1.5">Pixel ID</label>
              <div className="flex gap-2">
                <Input value="1737895096898715" readOnly className="bg-gray-700 border-gray-600 text-white font-mono" data-testid="pixel-id-input" />
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700 shrink-0" onClick={() => copyToClipboard('1737895096898715', 'pixel')}>
                  {copied === 'pixel' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="bg-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-300 mb-2">Eventos configurados:</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="border-gray-600 text-gray-300">PageView</Badge>
                <Badge variant="outline" className="border-orange-600 text-orange-400">Purchase (conversion)</Badge>
              </div>
              <p className="text-xs text-gray-500 mt-3">El evento <code className="text-orange-400">Purchase</code> envia <code className="text-gray-400">value</code>, <code className="text-gray-400">currency: MXN</code>, <code className="text-gray-400">content_ids</code> y <code className="text-gray-400">num_items</code> al completar una compra.</p>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
